<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OrdersAPI from 'dashboard/api/orders';
import ProductsAPI from 'dashboard/api/products';
import Modal from 'dashboard/components/Modal.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const props = defineProps({
  order: {
    type: Object,
    default: null,
  },
  show: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['close', 'save']);

const { t } = useI18n();

const showModal = computed({
  get: () => props.show,
  set: value => {
    if (!value) {
      emit('close');
    }
  },
});

const formData = ref({
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  status: 'pending',
  payment_status: 'pending',
  payment_method: 'credit_card',
  delivery_status: 'pending',
  tax: 0,
  shipping: 0,
  customer_note: '',
  internal_note: '',
  items: [],
});

const products = ref([]);
const saving = ref(false);
const activeTab = ref('general');
const showProductSelector = ref(false);
const selectedProducts = ref([]);

const isEditMode = computed(() => !!props.order);

const statusOptions = computed(() => [
  { label: t('WEBSITE.ORDERS.STATUS.PENDING'), value: 'pending' },
  { label: t('WEBSITE.ORDERS.STATUS.PROCESSING'), value: 'processing' },
  { label: t('WEBSITE.ORDERS.STATUS.COMPLETED'), value: 'completed' },
  { label: t('WEBSITE.ORDERS.STATUS.CANCELLED'), value: 'cancelled' },
]);

const paymentStatusOptions = computed(() => [
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.PENDING'), value: 'pending' },
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.PAID'), value: 'paid' },
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.FAILED'), value: 'failed' },
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.REFUNDED'), value: 'refunded' },
]);

const paymentMethodOptions = computed(() => [
  {
    label: t('WEBSITE.ORDERS.PAYMENT_METHOD.CREDIT_CARD'),
    value: 'credit_card',
  },
  { label: t('WEBSITE.ORDERS.PAYMENT_METHOD.DEBIT_CARD'), value: 'debit_card' },
  { label: t('WEBSITE.ORDERS.PAYMENT_METHOD.PAYPAL'), value: 'paypal' },
  {
    label: t('WEBSITE.ORDERS.PAYMENT_METHOD.BANK_TRANSFER'),
    value: 'bank_transfer',
  },
  { label: t('WEBSITE.ORDERS.PAYMENT_METHOD.CASH'), value: 'cash' },
  { label: t('WEBSITE.ORDERS.PAYMENT_METHOD.CHECK'), value: 'check' },
  { label: t('WEBSITE.ORDERS.PAYMENT_METHOD.OTHER'), value: 'other' },
]);

const deliveryStatusOptions = computed(() => [
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.PENDING'), value: 'pending' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.PREPARING'), value: 'preparing' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.SHIPPED'), value: 'shipped' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.DELIVERED'), value: 'delivered' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.CANCELLED'), value: 'cancelled' },
]);

const subtotal = computed(() => {
  return formData.value.items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
});

const total = computed(() => {
  return (
    subtotal.value +
    Number(formData.value.tax) +
    Number(formData.value.shipping)
  );
});

const fetchProducts = async () => {
  try {
    const response = await ProductsAPI.get();
    products.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.PRODUCTS_FETCH_FAILED'));
  }
};

onMounted(async () => {
  await fetchProducts();

  if (props.order) {
    formData.value = {
      customer_name: props.order.customer_name || '',
      customer_email: props.order.customer_email || '',
      customer_phone: props.order.customer_phone || '',
      status: props.order.status || 'pending',
      payment_status: props.order.payment_status || 'pending',
      payment_method: props.order.payment_method || 'credit_card',
      delivery_status: props.order.delivery_status || 'pending',
      tax: props.order.tax || 0,
      shipping: 0,
      customer_note: props.order.customer_note || '',
      internal_note: props.order.internal_note || '',
      items: props.order.items || [],
    };
  }
});

const removeItem = index => {
  formData.value.items.splice(index, 1);
};

const openProductSelector = () => {
  selectedProducts.value = [];
  showProductSelector.value = true;
};

const closeProductSelector = () => {
  showProductSelector.value = false;
  selectedProducts.value = [];
};

const toggleProduct = productId => {
  const index = selectedProducts.value.indexOf(productId);
  if (index > -1) {
    selectedProducts.value.splice(index, 1);
  } else {
    selectedProducts.value.push(productId);
  }
};

const addSelectedProducts = () => {
  selectedProducts.value.forEach(productId => {
    const product = products.value.find(p => p.id === productId);
    if (product) {
      // Vérifier si le produit n'est pas déjà dans la liste
      const exists = formData.value.items.some(
        item => item.product_id === product.id
      );
      if (!exists) {
        formData.value.items.push({
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku || '',
          price: product.price || 0,
          quantity: 1,
        });
      }
    }
  });
  closeProductSelector();
};

const formatPrice = price => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
};

const handleSubmit = async () => {
  if (!formData.value.customer_name) {
    useAlert(t('WEBSITE.ORDERS.VALIDATION.CUSTOMER_NAME_REQUIRED'));
    return;
  }

  if (formData.value.items.length === 0) {
    useAlert(t('WEBSITE.ORDERS.VALIDATION.ITEMS_REQUIRED'));
    return;
  }

  saving.value = true;

  try {
    const { items, ...orderData } = formData.value;
    const payload = {
      order: {
        ...orderData,
        order_items_attributes: items,
      },
    };

    if (isEditMode.value) {
      await OrdersAPI.update(props.order.id, payload);
      useAlert(t('WEBSITE.ORDERS.API.SUCCESS.ORDER_UPDATED'));
    } else {
      await OrdersAPI.create(payload);
      useAlert(t('WEBSITE.ORDERS.API.SUCCESS.ORDER_CREATED'));
    }

    emit('save');
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.SAVE_FAILED'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="showModal" size="xl" :on-close="() => emit('close')">
    <div class="p-6">
      <!-- Header -->
      <h2 class="text-lg font-semibold text-n-slate-12 mb-6">
        {{
          isEditMode
            ? t('WEBSITE.ORDERS.FORM.EDIT_TITLE')
            : t('WEBSITE.ORDERS.FORM.CREATE_TITLE')
        }}
      </h2>

      <!-- Tabs -->
      <div class="flex gap-1 mb-6 border-b border-n-weak">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'general'
              ? 'text-n-blue-11 border-b-2 border-n-blue-11'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeTab = 'general'"
        >
          {{ t('WEBSITE.ORDERS.FORM.TAB_GENERAL') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'items'
              ? 'text-n-blue-11 border-b-2 border-n-blue-11'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeTab = 'items'"
        >
          {{ t('WEBSITE.ORDERS.FORM.TAB_ITEMS') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'pricing'
              ? 'text-n-blue-11 border-b-2 border-n-blue-11'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeTab = 'pricing'"
        >
          {{ t('WEBSITE.ORDERS.FORM.TAB_PRICING') }}
        </button>
      </div>

      <!-- General Tab -->
      <div v-if="activeTab === 'general'" class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="formData.customer_name"
            :label="t('WEBSITE.ORDERS.FORM.CUSTOMER_NAME')"
            :placeholder="t('WEBSITE.ORDERS.FORM.CUSTOMER_NAME_PLACEHOLDER')"
            required
          />
          <Input
            v-model="formData.customer_email"
            type="email"
            :label="t('WEBSITE.ORDERS.FORM.CUSTOMER_EMAIL')"
            :placeholder="t('WEBSITE.ORDERS.FORM.CUSTOMER_EMAIL_PLACEHOLDER')"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="formData.customer_phone"
            :label="t('WEBSITE.ORDERS.FORM.CUSTOMER_PHONE')"
            :placeholder="t('WEBSITE.ORDERS.FORM.CUSTOMER_PHONE_PLACEHOLDER')"
          />
          <SelectMenu
            v-model="formData.payment_method"
            :options="paymentMethodOptions"
            :label="t('WEBSITE.ORDERS.FORM.PAYMENT_METHOD')"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <SelectMenu
            v-model="formData.status"
            :options="statusOptions"
            :label="t('WEBSITE.ORDERS.FORM.STATUS')"
          />
          <SelectMenu
            v-model="formData.payment_status"
            :options="paymentStatusOptions"
            :label="t('WEBSITE.ORDERS.FORM.PAYMENT_STATUS')"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <SelectMenu
            v-model="formData.delivery_status"
            :options="deliveryStatusOptions"
            :label="t('WEBSITE.ORDERS.FORM.DELIVERY_STATUS')"
          />
        </div>

        <TextArea
          v-model="formData.customer_note"
          :label="t('WEBSITE.ORDERS.FORM.CUSTOMER_NOTE')"
          :placeholder="t('WEBSITE.ORDERS.FORM.CUSTOMER_NOTE_PLACEHOLDER')"
          rows="3"
        />

        <TextArea
          v-model="formData.internal_note"
          :label="t('WEBSITE.ORDERS.FORM.INTERNAL_NOTE')"
          :placeholder="t('WEBSITE.ORDERS.FORM.INTERNAL_NOTE_PLACEHOLDER')"
          rows="3"
        />
      </div>

      <!-- Items Tab -->
      <div v-if="activeTab === 'items'" class="space-y-4">
        <!-- Bouton pour ajouter des produits -->
        <Button
          type="button"
          :label="t('WEBSITE.ORDERS.FORM.ADD_PRODUCTS')"
          icon="i-lucide-plus"
          variant="outline"
          @click="openProductSelector"
        />

        <!-- Liste des items de commande -->
        <div v-if="formData.items.length === 0" class="text-center py-8">
          <span class="i-lucide-package size-12 mx-auto mb-3 text-n-slate-10" />
          <p class="text-sm text-n-slate-10">
            {{ t('WEBSITE.ORDERS.FORM.NO_ITEMS') }}
          </p>
        </div>

        <div
          v-for="(item, index) in formData.items"
          :key="index"
          class="p-4 border rounded-lg border-n-weak bg-n-solid-1"
        >
          <div class="flex items-start gap-4">
            <div class="flex-1 space-y-3">
              <div class="text-sm font-medium text-n-slate-12">
                {{ item.product_name }}
              </div>

              <div class="grid grid-cols-2 gap-3">
                <Input
                  v-model.number="item.price"
                  type="number"
                  step="0.01"
                  :label="t('WEBSITE.ORDERS.FORM.PRICE')"
                  :placeholder="t('WEBSITE.ORDERS.FORM.PRICE_PLACEHOLDER')"
                  required
                />
                <Input
                  v-model.number="item.quantity"
                  type="number"
                  :label="t('WEBSITE.ORDERS.FORM.QUANTITY')"
                  :placeholder="t('WEBSITE.ORDERS.FORM.QUANTITY_PLACEHOLDER')"
                  required
                />
              </div>

              <div class="text-sm text-n-slate-10">
                {{ t('WEBSITE.ORDERS.FORM.ITEM_TOTAL') }}
                {{ formatPrice(item.price * item.quantity) }}
              </div>
            </div>

            <button
              type="button"
              class="p-2 rounded-lg text-n-red-11 hover:bg-n-red-3 transition-colors"
              @click="removeItem(index)"
            >
              <span class="i-lucide-trash-2 size-5" />
            </button>
          </div>
        </div>
      </div>

      <!-- Pricing Tab -->
      <div v-if="activeTab === 'pricing'" class="space-y-4">
        <div class="grid grid-cols-3 gap-4">
          <Input
            v-model.number="formData.tax"
            type="number"
            step="0.01"
            :label="t('WEBSITE.ORDERS.FORM.TAX')"
            :placeholder="t('WEBSITE.ORDERS.FORM.TAX_PLACEHOLDER')"
          />
        </div>

        <div class="p-4 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-n-slate-10">{{
                t('WEBSITE.ORDERS.FORM.SUBTOTAL')
              }}</span>
              <span class="font-medium text-n-slate-12">{{
                formatPrice(subtotal)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-10">{{
                t('WEBSITE.ORDERS.FORM.TAX')
              }}</span>
              <span class="text-n-slate-12">{{
                formatPrice(formData.tax)
              }}</span>
            </div>
            <div class="pt-2 border-t border-n-weak flex justify-between">
              <span class="font-semibold text-n-slate-12">{{
                t('WEBSITE.ORDERS.FORM.TOTAL')
              }}</span>
              <span class="text-lg font-bold text-n-blue-11">{{
                formatPrice(total)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="flex justify-end gap-2 pt-6 mt-6 border-t border-n-weak">
        <Button
          :label="t('WEBSITE.ORDERS.FORM.CANCEL')"
          variant="outline"
          @click="emit('close')"
        />
        <Button
          :label="
            isEditMode
              ? t('WEBSITE.ORDERS.FORM.UPDATE')
              : t('WEBSITE.ORDERS.FORM.CREATE')
          "
          :loading="saving"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Modal>

  <!-- Product Selector Modal -->
  <Modal
    v-if="showProductSelector"
    v-model:show="showProductSelector"
    size="lg"
    :on-close="closeProductSelector"
  >
    <div class="p-6">
      <!-- Header -->
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
        {{ t('WEBSITE.ORDERS.FORM.SELECT_PRODUCTS') }}
      </h3>

      <!-- Product List -->
      <div class="space-y-2 max-h-96 overflow-y-auto mb-6">
        <div
          v-for="product in products"
          :key="product.id"
          class="flex items-center gap-3 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-2 cursor-pointer transition-colors"
          @click="toggleProduct(product.id)"
        >
          <input
            type="checkbox"
            :checked="selectedProducts.includes(product.id)"
            class="w-4 h-4 text-n-blue-11 bg-n-solid-2 border-n-weak rounded focus:ring-n-blue-9 focus:ring-2"
            @click.stop="toggleProduct(product.id)"
          />
          <div class="flex-1">
            <div class="text-sm font-medium text-n-slate-12">
              {{ product.name }}
            </div>
            <div class="text-xs text-n-slate-10">
              {{ formatPrice(product.price) }}
            </div>
          </div>
        </div>

        <div v-if="products.length === 0" class="text-center py-8">
          <span class="i-lucide-package size-12 mx-auto mb-3 text-n-slate-10" />
          <p class="text-sm text-n-slate-10">
            {{ t('WEBSITE.ORDERS.FORM.NO_PRODUCTS') }}
          </p>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="flex justify-end gap-2 pt-4 border-t border-n-weak">
        <Button
          :label="t('WEBSITE.ORDERS.FORM.CANCEL')"
          variant="outline"
          @click="closeProductSelector"
        />
        <Button
          :label="t('WEBSITE.ORDERS.FORM.ADD_SELECTED')"
          :disabled="selectedProducts.length === 0"
          @click="addSelectedProducts"
        />
      </div>
    </div>
  </Modal>
</template>
